import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { BarChart, CandlestickChart, LineChart } from 'echarts/charts';
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  LegendScrollComponent,
  MarkPointComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  LineChart,
  BarChart,
  CandlestickChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  // Прокручиваемая легенда (legend.type='scroll') — на телефоне легенда в
  // одну строку со стрелками, вместо переноса поверх графика.
  LegendScrollComponent,
  DataZoomComponent,
  // Без явной регистрации markPoint молча игнорируется (tree-shaking
  // echarts/core): маркеры расхождений на тренде просто не рисуются.
  MarkPointComponent,
  CanvasRenderer,
]);

export type EChartsOption = echarts.EChartsCoreOption;

/** Сенсорный экран (палец, а не мышь) — тап заменяет hover. */
export function isCoarsePointer(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
}

interface Props {
  option: EChartsOption;
  height?: number;
  /** eventName → handler (например { click: (params) => ... }). */
  onEvents?: Record<string, (params: unknown) => void>;
  /**
   * Клик по ЛЮБОМУ месту области графика с индексом ближайшей точки по X.
   *
   * Обычный `onEvents.click` серии срабатывает лишь при попадании курсора
   * ровно в символ/линию, а у нас `showSymbol: false` — попасть почти
   * невозможно. Поэтому слушаем zrender-клик и переводим пиксель в
   * координату данных (`convertFromPixel`), получая индекс категории.
   *
   * На касаниях тап — единственный способ увидеть тултип, поэтому действие
   * срабатывает только на ПОВТОРНЫЙ тап в ту же точку (первый — показывает
   * тултип). Мышь — по первому клику, как раньше.
   */
  onGridClick?: (dataIndex: number) => void;
}

/**
 * Тонкий враппер echarts/core: init/dispose, setOption при изменении,
 * resize через ResizeObserver. ВАЖНО: canvas-рендерер НЕ резолвит строки
 * `var(--x)` — цвета в option'ах должны быть уже вычисленными (hex/rgb);
 * токены темы резолвятся в CHART_COLORS (см. balances/ChartsPanel.tsx).
 */
export function EChart({ option, height = 320, onEvents, onGridClick }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    const chart = echarts.init(ref.current);
    chartRef.current = chart;
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.setOption(option, { notMerge: true });
  }, [option]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !onEvents) return;
    for (const [name, fn] of Object.entries(onEvents)) chart.on(name, fn);
    return () => {
      for (const [name, fn] of Object.entries(onEvents)) chart.off(name, fn);
    };
  }, [onEvents]);

  useEffect(() => {
    const chart = chartRef.current;
    const el = ref.current;
    if (!chart || !el || !onGridClick) return;

    /** Пиксель (относительно контейнера) → индекс категории; null — вне области построения. */
    const toIndex = (x: number, y: number): number | null => {
      const point: [number, number] = [x, y];
      // Клик вне области построения (легенда, слайдер зума, поля) игнорируем.
      if (!chart.containPixel('grid', point)) return null;
      const [dataIndex] = chart.convertFromPixel({ seriesIndex: 0 }, point) as [number, number];
      if (typeof dataIndex !== 'number' || Number.isNaN(dataIndex)) return null;
      return Math.round(dataIndex);
    };

    // Мышь: клик zrender, сразу действие (как было).
    let lastTouchAt = 0;
    const zr = chart.getZr();
    const onZrClick = (e: { offsetX: number; offsetY: number }) => {
      // Клик, синтезированный браузером из касания, обрабатывает тач-ветка ниже.
      if (Date.now() - lastTouchAt < 800) return;
      const index = toIndex(e.offsetX, e.offsetY);
      if (index !== null) onGridClick(index);
    };
    zr.on('click', onZrClick);

    // Палец: свой распознаватель тапа на pointer-событиях (синтетический click
    // zrender от касаний ненадёжен — на повторный тап не приходит). Тап =
    // короткое касание без сдвига (свайп/зум графика — не тап). Действие —
    // только на ПОВТОРНЫЙ тап в ту же точку: первый тап показывает тултип.
    let down: { x: number; y: number; at: number } | null = null;
    let lastTap: { index: number; at: number } | null = null;
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      down = { x: e.clientX, y: e.clientY, at: Date.now() };
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !down) return;
      const d = down;
      down = null;
      lastTouchAt = Date.now();
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 10 || Date.now() - d.at > 600) return;
      const r = el.getBoundingClientRect();
      const index = toIndex(e.clientX - r.left, e.clientY - r.top);
      if (index === null) return;
      const now = Date.now();
      const repeat = lastTap !== null && lastTap.index === index && now - lastTap.at < 5000;
      lastTap = { index, at: now };
      if (!repeat) return;
      lastTap = null;
      onGridClick(index);
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    return () => {
      zr.off('click', onZrClick);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
    };
  }, [onGridClick]);

  return <div ref={ref} style={{ height, width: '100%' }} />;
}
